/**
 * All contextual editor menus for the RichTextField TipTap editor.
 *
 * Includes: main BubbleMenu (text selection), table BubbleMenu,
 * columns BubbleMenu, FloatingMenu (empty paragraph), DragHandle,
 * and reusable UI primitives (BubbleBtn, BDropdown, etc.).
 *
 * No toolbar is rendered statically — all tools appear contextually.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BubbleMenu, FloatingMenu } from '@tiptap/react/menus';
import { DragHandle } from '@tiptap/extension-drag-handle-react';
import { NodeSelection } from '@tiptap/pm/state';
import {
    DEFAULT_FEATURES,
    HEADING_ICONS,
    HEADING_LABELS,
    HIGHLIGHT_COLORS,
    CELL_BG_COLORS,
    FONT_FAMILIES,
    FONT_SIZES,
    TEXT_COLORS,
    COLUMN_LAYOUTS,
    isEditorSafeUrl,
} from './rich-text-constants';
import { setSlashContext } from './slash-commands';
import {
    AlignCenter,
    AlignJustify,
    AlignLeft,
    AlignRight,
    Bold,
    Calendar,
    CheckSquare,
    ChevronDown,
    ChevronRight,
    Code,
    Columns2,
    Columns3,
    Ellipsis,
    GripVertical,
    Highlighter,
    ImageIcon,
    Info,
    Italic,
    Link as LinkIcon,
    List,
    ListOrdered,
    Minus,
    Palette,
    Paperclip,
    Pilcrow,
    Plus,
    Redo2,
    RemoveFormatting,
    Smile,
    Strikethrough,
    SubscriptIcon,
    SuperscriptIcon,
    Table as TableIcon,
    Tag,
    Terminal,
    TextQuote,
    Trash2,
    UnderlineIcon,
    Undo2,
} from 'lucide-react';

/* ================================================================
   BubbleMenu / FloatingMenu primitives (Tailwind)
   ================================================================ */

function BubbleBtn({ onClick, isActive, disabled, title, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            aria-label={title}
            className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer
                ${isActive ? 'bg-blue-50 text-blue-700' : 'text-stone-600 hover:bg-stone-100'}
                ${disabled ? 'opacity-30 !cursor-default' : ''}`}
        >
            {children}
        </button>
    );
}

const BSep = () => <span className="w-px h-5 bg-stone-200 mx-0.5 shrink-0" />;

function BDropdown({ trigger, children, title }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const close = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    return (
        <div ref={ref} className="relative inline-flex">
            <button
                type="button"
                title={title}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}
                className="inline-flex items-center justify-center gap-px px-1 h-8 rounded-lg text-stone-600 hover:bg-stone-100 transition-colors cursor-pointer text-xs"
            >
                {trigger}
            </button>
            {open && (
                <div
                    className="absolute top-full left-0 mt-1 z-50 min-w-[180px] bg-white border border-stone-200 rounded-xl shadow-xl p-1 flex flex-col"
                    onClick={() => setOpen(false)}
                >
                    {children}
                </div>
            )}
        </div>
    );
}

const BDropItem = ({ onClick, className, children }) => (
    <button
        type="button"
        onClick={onClick}
        className={`flex items-center gap-2 px-2.5 py-1.5 text-xs text-stone-600 rounded-lg hover:bg-stone-100 cursor-pointer w-full text-left whitespace-nowrap transition-colors ${className || ''}`}
    >
        {children}
    </button>
);

const BDropDivider = () => <div className="h-px bg-stone-200 my-1 mx-1.5" />;

const BDropLabel = ({ children }) => (
    <div className="px-2.5 pt-1.5 pb-0.5 text-2xs font-semibold text-stone-400 uppercase tracking-wider select-none">
        {children}
    </div>
);

const FloatingIconBtn = ({ onClick, title, children }) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        aria-label={title}
        className="w-7 h-7 inline-flex items-center justify-center rounded-md text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors cursor-pointer"
    >
        {children}
    </button>
);

/* ================================================================
   Editor menus: BubbleMenus + FloatingMenu + DragHandle
   ================================================================ */

/**
 * Renders all contextual editor menus (BubbleMenus, FloatingMenu, DragHandle).
 * Syncs the active feature set into the slash-commands module via setSlashContext().
 *
 * @param {{ editor: import('@tiptap/react').Editor }} props.editor - TipTap editor instance.
 * @param {string[]|undefined} props.allowedFeatures - Feature flags controlling which buttons appear.
 * @param {Function|null} props.onUploadFile - Upload handler; enables image/file buttons when truthy.
 */
export default function EditorMenus({ editor, allowedFeatures, onUploadFile }) {
    const [linkMode, setLinkMode] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const linkInputRef = useRef(null);
    const [floatingOpen, setFloatingOpen] = useState(false);
    const floatingPanelRef = useRef(null);
    const [menuExpanded, setMenuExpanded] = useState(false);
    const fileInputRef = useRef(null);
    useEffect(() => {
        if (!floatingOpen) return;
        const handler = (e) => {
            if (floatingPanelRef.current && !floatingPanelRef.current.contains(e.target)) {
                setFloatingOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [floatingOpen]);

    const features = useMemo(() => new Set(allowedFeatures ?? DEFAULT_FEATURES), [allowedFeatures]);

    useEffect(() => {
        setSlashContext({ features });
        return () => setSlashContext({ features: null });
    }, [features]);

    const triggerEmojiSuggestion = useCallback(() => {
        editor?.chain().focus().insertContent(':').run();
    }, [editor]);

    const insertInlineDate = useCallback(() => {
        editor?.chain().focus().insertContent({
            type: 'inlineDate',
            attrs: { date: new Date().toISOString().slice(0, 10) },
        }).run();
    }, [editor]);

    const insertStatusBadge = useCallback(() => {
        editor?.chain().focus().insertContent({
            type: 'statusBadge',
            attrs: { label: 'Statut', color: 'default' },
        }).run();
    }, [editor]);

    const iconSize = 15;

    const openLink = useCallback(() => {
        const existing = editor?.getAttributes('link').href ?? '';
        setLinkUrl(existing);
        setLinkMode(true);
        setTimeout(() => linkInputRef.current?.focus(), 50);
    }, [editor]);

    const applyLink = useCallback(() => {
        if (!editor) return;
        const trimmed = linkUrl.trim();
        if (trimmed) {
            if (!isEditorSafeUrl(trimmed)) return;
            editor.chain().focus().setLink({ href: trimmed }).run();
        } else {
            editor.chain().focus().unsetLink().run();
        }
        setLinkMode(false);
        setLinkUrl('');
    }, [editor, linkUrl]);

    const removeLink = useCallback(() => {
        editor?.chain().focus().unsetLink().run();
        setLinkMode(false);
        setLinkUrl('');
    }, [editor]);

    const handleLinkKeyDown = useCallback((e) => {
        if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
        if (e.key === 'Escape') { setLinkMode(false); setLinkUrl(''); }
    }, [applyLink]);

    const triggerFilePicker = useCallback((accept) => {
        if (!fileInputRef.current) return;
        fileInputRef.current.accept = accept || '';
        fileInputRef.current.value = '';
        fileInputRef.current.click();
    }, []);

    const handleFileInputChange = useCallback((e) => {
        const files = Array.from(e.target.files || []);
        for (const file of files) {
            onUploadFile?.(editor, file);
        }
    }, [editor, onUploadFile]);

    const insertImage = useCallback(() => {
        if (onUploadFile) {
            triggerFilePicker('image/*');
        } else {
            const url = window.prompt('URL de l\'image :');
            if (!url || !isEditorSafeUrl(url)) return;
            const alt = window.prompt('Texte alternatif (optionnel) :') || '';
            editor?.chain().focus().setImage({ src: url, alt: alt.trim() || undefined }).run();
        }
    }, [editor, onUploadFile, triggerFilePicker]);

    const insertFile = useCallback(() => {
        if (onUploadFile) {
            triggerFilePicker('');
        }
    }, [onUploadFile, triggerFilePicker]);

    const clearFormatting = useCallback(() => {
        editor?.chain().focus().clearNodes().unsetAllMarks().run();
    }, [editor]);

    const blockTypeIcon = () => {
        for (let l = 1; l <= 6; l++) {
            if (editor.isActive('heading', { level: l })) return HEADING_ICONS[l - 1];
        }
        return Pilcrow;
    };

    const BlockTypeDropdown = features.has('heading') ? (
        <BDropdown
            title="Type de bloc"
            trigger={<>{(() => { const I = blockTypeIcon(); return <I size={iconSize} />; })()}<ChevronDown size={10} /></>}
        >
            <BDropItem onClick={() => editor.chain().focus().setParagraph().run()}>
                <Pilcrow size={14} /> Paragraphe
            </BDropItem>
            {[1, 2, 3, 4, 5, 6].map((level) => {
                const HIcon = HEADING_ICONS[level - 1];
                return (
                    <BDropItem key={level} onClick={() => editor.chain().focus().toggleHeading({ level }).run()}>
                        <HIcon size={14} /> {HEADING_LABELS[level]}
                    </BDropItem>
                );
            })}
        </BDropdown>
    ) : null;

    const formatBtns = (
        <>
            {features.has('bold') && <BubbleBtn onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Gras (Ctrl+B)"><Bold size={iconSize} /></BubbleBtn>}
            {features.has('italic') && <BubbleBtn onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italique (Ctrl+I)"><Italic size={iconSize} /></BubbleBtn>}
            {features.has('underline') && <BubbleBtn onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Souligné (Ctrl+U)"><UnderlineIcon size={iconSize} /></BubbleBtn>}
            {features.has('strike') && <BubbleBtn onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Barré"><Strikethrough size={iconSize} /></BubbleBtn>}
            {features.has('code') && <BubbleBtn onClick={() => editor.chain().focus().toggleCode().run()} isActive={editor.isActive('code')} title="Code inline"><Code size={iconSize} /></BubbleBtn>}
        </>
    );

    const linkInput = (
        <div className="flex items-center gap-1.5 p-1">
            <LinkIcon size={14} className="text-stone-400 shrink-0 ml-1" />
            <input
                ref={linkInputRef}
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={handleLinkKeyDown}
                placeholder="https://…"
                className="flex-1 min-w-[200px] border border-blue-200 rounded-lg px-2.5 py-1 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15"
            />
            <button type="button" onClick={applyLink} className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                Appliquer
            </button>
            {editor.isActive('link') && (
                <button type="button" onClick={removeLink} className="px-3 py-1 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">
                    Supprimer
                </button>
            )}
            <button type="button" onClick={() => { setLinkMode(false); setLinkUrl(''); }} className="w-7 h-7 inline-flex items-center justify-center rounded-lg text-stone-500 hover:bg-stone-100 transition-colors cursor-pointer">
                ✕
            </button>
        </div>
    );

    return (
        <>
            {/* ── Main BubbleMenu: text selection ── */}
            <BubbleMenu
                editor={editor}
                pluginKey="mainMenu"
                shouldShow={({ editor: ed, from, to, state }) => {
                    if (from === to) return false;
                    if (ed.isActive('codeBlock')) return false;
                    if (state.selection instanceof NodeSelection) return false;
                    return true;
                }}
                updateDelay={100}
                options={{ placement: 'top', offset: 8 }}
                className="bg-white rounded-xl shadow-xl border border-stone-200"
            >
                {linkMode ? linkInput : (
                    <div className="flex flex-col">
                        {/* ── Primary toolbar ── */}
                        <div className="flex items-center gap-0.5 p-1">
                            {BlockTypeDropdown}
                            {BlockTypeDropdown && <BSep />}
                            {formatBtns}
                            <BSep />
                            {features.has('link') && (
                                <BubbleBtn onClick={openLink} isActive={editor.isActive('link')} title="Lien">
                                    <LinkIcon size={iconSize} />
                                </BubbleBtn>
                            )}
                            {features.has('highlight') && (
                                <BDropdown title="Surlignage" trigger={<><Highlighter size={13} /><ChevronDown size={10} /></>}>
                                    {HIGHLIGHT_COLORS.map((c) => (
                                        <BDropItem key={c.value} onClick={() => editor.chain().focus().toggleHighlight({ color: c.value }).run()}>
                                            <span className="inline-block w-3.5 h-3.5 rounded-sm shrink-0" style={{ background: c.value }} /> {c.label}
                                        </BDropItem>
                                    ))}
                                    <BDropDivider />
                                    <BDropItem onClick={() => editor.chain().focus().unsetHighlight().run()}>
                                        <RemoveFormatting size={12} /> Aucun
                                    </BDropItem>
                                </BDropdown>
                            )}
                            <BSep />
                            {features.has('textAlign') && (
                                <>
                                    <BubbleBtn onClick={() => editor.chain().focus().setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} title="Gauche"><AlignLeft size={iconSize} /></BubbleBtn>
                                    <BubbleBtn onClick={() => editor.chain().focus().setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} title="Centre"><AlignCenter size={iconSize} /></BubbleBtn>
                                    <BubbleBtn onClick={() => editor.chain().focus().setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} title="Droite"><AlignRight size={iconSize} /></BubbleBtn>
                                    <BSep />
                                </>
                            )}
                            <BubbleBtn onClick={clearFormatting} title="Effacer mise en forme"><RemoveFormatting size={iconSize} /></BubbleBtn>
                            <BSep />
                            <BubbleBtn
                                onClick={() => setMenuExpanded(prev => !prev)}
                                isActive={menuExpanded}
                                title={menuExpanded ? 'Moins d\'options' : 'Plus d\'options'}
                            >
                                <Ellipsis size={iconSize} />
                            </BubbleBtn>
                        </div>
                        {/* ── Extended toolbar ── */}
                        {menuExpanded && (
                            <div className="flex flex-wrap items-center gap-0.5 px-1 pb-1 pt-1 border-t border-stone-100 max-w-[720px]">
                                {features.has('subscript') && <BubbleBtn onClick={() => editor.chain().focus().toggleSubscript().run()} isActive={editor.isActive('subscript')} title="Indice"><SubscriptIcon size={iconSize} /></BubbleBtn>}
                                {features.has('superscript') && <BubbleBtn onClick={() => editor.chain().focus().toggleSuperscript().run()} isActive={editor.isActive('superscript')} title="Exposant"><SuperscriptIcon size={iconSize} /></BubbleBtn>}
                                <BSep />
                                <BDropdown title="Police" trigger={<span className="text-xs font-medium px-0.5 max-w-[80px] truncate">{editor.getAttributes('textStyle').fontFamily ? 'Abc' : 'Police'}</span>}>
                                    {FONT_FAMILIES.map((f) => (
                                        <BDropItem key={f.value} onClick={() => f.value ? editor.chain().focus().setFontFamily(f.value).run() : editor.chain().focus().unsetFontFamily().run()}>
                                            <span style={{ fontFamily: f.value || 'inherit' }}>{f.label}</span>
                                        </BDropItem>
                                    ))}
                                </BDropdown>
                                <BDropdown title="Taille" trigger={<span className="text-xs font-medium px-0.5">{editor.getAttributes('textStyle').fontSize || '16'}</span>}>
                                    {FONT_SIZES.map((s) => (
                                        <BDropItem key={s.value} onClick={() => editor.chain().focus().setFontSize(s.value).run()}>
                                            <span style={{ fontSize: s.value }}>{s.label}px</span>
                                        </BDropItem>
                                    ))}
                                    <BDropDivider />
                                    <BDropItem onClick={() => editor.chain().focus().unsetFontSize().run()}>
                                        <RemoveFormatting size={12} /> Défaut
                                    </BDropItem>
                                </BDropdown>
                                <BSep />
                                {features.has('color') && (
                                    <BDropdown title="Couleur du texte" trigger={<><Palette size={13} /><ChevronDown size={10} /></>}>
                                        {TEXT_COLORS.map((c) => (
                                            <BDropItem key={c.value || 'default'} onClick={() => c.value ? editor.chain().focus().setColor(c.value).run() : editor.chain().focus().unsetColor().run()}>
                                                <span className="inline-block w-3.5 h-3.5 rounded-sm shrink-0" style={{ background: c.value || '#44403c', border: !c.value ? '1px solid #d6d3d1' : 'none' }} /> {c.label}
                                            </BDropItem>
                                        ))}
                                    </BDropdown>
                                )}
                                {features.has('textAlign') && (
                                    <>
                                        <BubbleBtn onClick={() => editor.chain().focus().setTextAlign('justify').run()} isActive={editor.isActive({ textAlign: 'justify' })} title="Justifier"><AlignJustify size={iconSize} /></BubbleBtn>
                                        <BSep />
                                    </>
                                )}
                                {features.has('bulletList') && <BubbleBtn onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Liste puces"><List size={iconSize} /></BubbleBtn>}
                                {features.has('orderedList') && <BubbleBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Liste numérotée"><ListOrdered size={iconSize} /></BubbleBtn>}
                                {features.has('taskList') && <BubbleBtn onClick={() => editor.chain().focus().toggleTaskList().run()} isActive={editor.isActive('taskList')} title="Tâches"><CheckSquare size={iconSize} /></BubbleBtn>}
                                <BSep />
                                {features.has('blockquote') && <BubbleBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="Citation"><TextQuote size={iconSize} /></BubbleBtn>}
                                {features.has('codeBlock') && <BubbleBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} isActive={editor.isActive('codeBlock')} title="Code"><Terminal size={iconSize} /></BubbleBtn>}
                                {features.has('horizontalRule') && <BubbleBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Séparateur"><Minus size={iconSize} /></BubbleBtn>}
                                <BSep />
                                {features.has('image') && <BubbleBtn onClick={insertImage} title="Image"><ImageIcon size={iconSize} /></BubbleBtn>}
                                {onUploadFile && <BubbleBtn onClick={insertFile} title="Fichier"><Paperclip size={iconSize} /></BubbleBtn>}
                                {features.has('emoji') && <BubbleBtn onClick={triggerEmojiSuggestion} title="Emoji"><Smile size={iconSize} /></BubbleBtn>}
                                {features.has('inlineDate') && <BubbleBtn onClick={insertInlineDate} title="Date"><Calendar size={iconSize} /></BubbleBtn>}
                                {features.has('statusBadge') && <BubbleBtn onClick={insertStatusBadge} title="Statut"><Tag size={iconSize} /></BubbleBtn>}
                                <BSep />
                                <BubbleBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Annuler (Ctrl+Z)"><Undo2 size={iconSize} /></BubbleBtn>
                                <BubbleBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rétablir (Ctrl+Y)"><Redo2 size={iconSize} /></BubbleBtn>
                            </div>
                        )}
                    </div>
                )}
            </BubbleMenu>

            {/* ── Table BubbleMenu: cursor in table, no text selection ── */}
            {features.has('table') && (
                <BubbleMenu
                    editor={editor}
                    pluginKey="tableMenu"
                    shouldShow={({ editor: ed, from, to }) => {
                        return from === to && ed.isActive('table');
                    }}
                    updateDelay={100}
                    options={{ placement: 'top', offset: 8 }}
                    className="bg-white rounded-xl shadow-xl border border-stone-200"
                >
                    <div className="flex flex-wrap items-center gap-0.5 p-1 max-w-[600px]">
                        <BubbleBtn onClick={() => editor.chain().focus().addColumnAfter().run()} title="Ajouter colonne"><Columns3 size={iconSize} /></BubbleBtn>
                        <BubbleBtn onClick={() => editor.chain().focus().addRowAfter().run()} title="Ajouter ligne"><Plus size={iconSize} /></BubbleBtn>
                        <BSep />
                        <BDropdown title="Options du tableau" trigger={<><TableIcon size={13} /><ChevronDown size={10} /></>}>
                            <BDropItem onClick={() => editor.chain().focus().deleteColumn().run()}>Supprimer colonne</BDropItem>
                            <BDropItem onClick={() => editor.chain().focus().deleteRow().run()}>Supprimer ligne</BDropItem>
                            <BDropDivider />
                            <BDropItem onClick={() => editor.chain().focus().mergeCells().run()}>Fusionner cellules</BDropItem>
                            <BDropItem onClick={() => editor.chain().focus().splitCell().run()}>Scinder cellule</BDropItem>
                            <BDropDivider />
                            <BDropItem onClick={() => editor.chain().focus().toggleHeaderRow().run()}>En-tête ligne</BDropItem>
                            <BDropItem onClick={() => editor.chain().focus().toggleHeaderColumn().run()}>En-tête colonne</BDropItem>
                            <BDropDivider />
                            <BDropLabel>Fond de cellule</BDropLabel>
                            <div className="flex flex-wrap gap-1 px-2.5 pb-2">
                                {CELL_BG_COLORS.map((c) => (
                                    <button
                                        key={c.label}
                                        type="button"
                                        title={c.label}
                                        onClick={() => editor.chain().focus().setCellAttribute('backgroundColor', c.value).run()}
                                        className="w-6 h-6 rounded border border-stone-300 hover:ring-2 hover:ring-blue-400 transition-shadow cursor-pointer flex items-center justify-center"
                                        style={{ background: c.value || '#fff' }}
                                    >
                                        {!c.value && <Minus size={10} className="text-stone-400" />}
                                    </button>
                                ))}
                            </div>
                            <BDropDivider />
                            <BDropItem onClick={() => editor.chain().focus().deleteTable().run()} className="!text-red-600 hover:!bg-red-50">
                                <Trash2 size={12} /> Supprimer le tableau
                            </BDropItem>
                        </BDropdown>
                    </div>
                </BubbleMenu>
            )}

            {/* ── Columns BubbleMenu: layout presets, add/remove column ── */}
            {features.has('columns') && (
                <BubbleMenu
                    editor={editor}
                    pluginKey="columnsMenu"
                    shouldShow={({ editor: ed, from, to }) => {
                        return from === to && ed.isActive('columns');
                    }}
                    updateDelay={100}
                    options={{ placement: 'top', offset: 8 }}
                    className="bg-white rounded-xl shadow-xl border border-stone-200"
                >
                    <div className="flex flex-col gap-1 p-1.5">
                        <div className="flex items-center gap-0.5">
                            {Object.entries(COLUMN_LAYOUTS).map(([key, cfg]) => {
                                const Icon = cfg.icon;
                                const isActive = editor.getAttributes('columns').layout === key;
                                return (
                                    <BubbleBtn
                                        key={key}
                                        onClick={() => {
                                            editor.chain().focus()
                                                .setColumnsLayout(key)
                                                .run();
                                        }}
                                        title={cfg.label}
                                        className={isActive ? '!bg-blue-100 !text-blue-700' : ''}
                                    >
                                        <Icon size={iconSize} />
                                    </BubbleBtn>
                                );
                            })}
                            <BSep />
                            <BubbleBtn
                                onClick={() => editor.chain().focus().addColumn().run()}
                                title="Ajouter une colonne"
                                disabled={(() => {
                                    const attrs = editor.getAttributes('columns');
                                    return (attrs.columns || 2) >= 4;
                                })()}
                            >
                                <Plus size={iconSize} />
                            </BubbleBtn>
                            <BubbleBtn
                                onClick={() => editor.chain().focus().removeColumn().run()}
                                title="Supprimer la dernière colonne"
                                disabled={(() => {
                                    const attrs = editor.getAttributes('columns');
                                    return (attrs.columns || 2) <= 2;
                                })()}
                            >
                                <Minus size={iconSize} />
                            </BubbleBtn>
                            <BSep />
                            <BubbleBtn
                                onClick={() => editor.chain().focus().deleteColumns().run()}
                                title="Supprimer le bloc colonnes"
                                className="!text-red-500 hover:!bg-red-50"
                            >
                                <Trash2 size={iconSize} />
                            </BubbleBtn>
                        </div>
                    </div>
                </BubbleMenu>
            )}

            {/* ── FloatingMenu: placeholder + quick-action icons on empty paragraphs ── */}
            <FloatingMenu
                editor={editor}
                shouldShow={({ view, state }) => {
                    // A custom shouldShow REPLACES TipTap's default, whose first
                    // test was the focus gate. Without replaying it here the bar
                    // shows next to the unfocused CSS placeholder = hint twice.
                    if (!view.hasFocus()) return false;
                    const { $from } = state.selection;
                    if ($from.parent.type.name !== 'paragraph') return false;
                    if (!state.selection.empty) return false;
                    return $from.parent.content.size === 0;
                }}
                options={{ placement: 'right-start', offset: 0 }}
                className="z-10"
                style={{ pointerEvents: 'none' }}
            >
                <div className="flex items-center gap-2" style={{ pointerEvents: 'none' }}>
                    <span className="text-stone-400 text-sm select-none whitespace-nowrap">
                        Tapez « / » pour les commandes
                    </span>
                    <div
                        className="flex items-center gap-px shrink-0"
                        style={{ pointerEvents: 'auto' }}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        {features.has('image') && <FloatingIconBtn onClick={insertImage} title="Image"><ImageIcon size={15} /></FloatingIconBtn>}
                        {onUploadFile && <FloatingIconBtn onClick={insertFile} title="Fichier"><Paperclip size={15} /></FloatingIconBtn>}
                        {features.has('codeBlock') && <FloatingIconBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Bloc de code"><Terminal size={15} /></FloatingIconBtn>}
                        {features.has('bulletList') && <FloatingIconBtn onClick={() => editor.chain().focus().toggleBulletList().run()} title="Liste à puces"><List size={15} /></FloatingIconBtn>}
                        {features.has('orderedList') && <FloatingIconBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Liste numérotée"><ListOrdered size={15} /></FloatingIconBtn>}
                        {features.has('taskList') && <FloatingIconBtn onClick={() => editor.chain().focus().toggleTaskList().run()} title="Check-list"><CheckSquare size={15} /></FloatingIconBtn>}
                        {features.has('table') && <FloatingIconBtn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Tableau"><TableIcon size={15} /></FloatingIconBtn>}
                        {features.has('emoji') && <FloatingIconBtn onClick={triggerEmojiSuggestion} title="Emoji"><Smile size={15} /></FloatingIconBtn>}
                        {features.has('inlineDate') && <FloatingIconBtn onClick={insertInlineDate} title="Date"><Calendar size={15} /></FloatingIconBtn>}
                        {features.has('statusBadge') && <FloatingIconBtn onClick={insertStatusBadge} title="Statut"><Tag size={15} /></FloatingIconBtn>}
                        <span className="w-px h-4 bg-stone-200 mx-0.5 shrink-0" />
                        <div className="relative" ref={floatingPanelRef}>
                            <FloatingIconBtn onClick={() => setFloatingOpen(!floatingOpen)} title="Plus d'options">
                                <Plus size={15} className={`transition-transform duration-200 ${floatingOpen ? 'rotate-45' : ''}`} />
                            </FloatingIconBtn>
                            {floatingOpen && (
                                <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-xl border border-stone-200 p-1.5 min-w-[200px] pointer-events-auto">
                                    {features.has('columns') && <BDropItem onClick={() => { editor.commands.insertColumns({ columns: 2, layout: 'equal-2' }); setFloatingOpen(false); }}><Columns2 size={14} /> Colonnes</BDropItem>}
                                    {features.has('callout') && <BDropItem onClick={() => { editor.commands.insertCallout({ type: 'info' }); setFloatingOpen(false); }}><Info size={14} /> Callout</BDropItem>}
                                    {features.has('details') && <BDropItem onClick={() => { editor.commands.insertDetails({ summary: 'Cliquer pour développer' }); setFloatingOpen(false); }}><ChevronRight size={14} /> Bloc repliable</BDropItem>}
                                    {features.has('blockquote') && <BDropItem onClick={() => { editor.chain().focus().toggleBlockquote().run(); setFloatingOpen(false); }}><TextQuote size={14} /> Citation</BDropItem>}
                                    {features.has('horizontalRule') && <BDropItem onClick={() => { editor.chain().focus().setHorizontalRule().run(); setFloatingOpen(false); }}><Minus size={14} /> Séparateur</BDropItem>}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </FloatingMenu>

            {/* ── DragHandle: grip icon for block reordering ── */}
            <DragHandle editor={editor} nested>
                <div className="flex items-center justify-center w-5 h-5 rounded text-stone-300 hover:text-stone-500 cursor-grab transition-opacity">
                    <GripVertical size={14} />
                </div>
            </DragHandle>

            {onUploadFile && (
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileInputChange}
                    tabIndex={-1}
                />
            )}
        </>
    );
}
